import React, { useEffect } from 'react'

import { Box, Button, Text, Tooltip } from '@chakra-ui/react'

import { dateFromTimestamp } from '@/helpers/date'
import { useCandidate } from '@/react-query/candidate'
import { useCompany } from '@/react-query/company'
import {
  useInterview,
  useRegenerateSummary,
} from '@/react-query/interview/interview'
import { useRole } from '@/react-query/role'
import { useIsViewOnlyMember } from '@/react-query/team'
import { MagicStarsIcon } from '@/views/assets/MagicStarsIcon'
import { InputTranscript } from '@/views/screens/Interview/components/FullTranscript/InputTranscript'
import { ShortTranscript } from '@/views/screens/Interview/components/ShortTranscript/ShortTranscript'
import { Summary } from '@/views/screens/Interview/components/Summary'

export const InterviewCard: React.FC = () => {
  const role = useRole()
  const candidate = useCandidate()
  const interview = useInterview().data
  const company = useCompany()
  const generateSummary = useRegenerateSummary()
  const summaryParts = interview?.SummaryParts

  const isViewOnlyUser = useIsViewOnlyMember()
  const isShortTranscriptDisabled =
    !interview?.fullTranscript ||
    (isViewOnlyUser && !interview?.shortTranscript)
  const interviewType = interview?.type

  // A scorecard needs a candidate and a transcript. A linked job is optional —
  // without one the summary is built from the meeting title instead. The short
  // transcript is generated lazily, so it must not gate the scorecard either.
  const isReportDisabled =
    interviewType === 'INTAKE'
      ? !interview?.shortTranscript || !role.data || !company.data
      : !candidate.data || !interview?.fullTranscript

  const hasScorecard = !!summaryParts?.length
  const hasShortTranscript = !!interview?.shortTranscript

  const getInitialTab = () => {
    if (hasScorecard) {
      return 'report'
    } else if (hasShortTranscript) {
      return 'short'
    }
    return 'full'
  }

  const [currentTab, setCurrentTab] = React.useState<
    'full' | 'short' | 'report'
  >(getInitialTab())

  // Follow the richest available view, but never pull the user out of the
  // scorecard they opened themselves — an empty scorecard explains why it is
  // empty and offers to generate it.
  useEffect(() => {
    if (hasScorecard) {
      setCurrentTab('report')
      return
    }
    setCurrentTab((tab) => {
      if (tab === 'report') return tab
      return hasShortTranscript ? 'short' : 'full'
    })
  }, [hasScorecard, hasShortTranscript])

  return (
    <Box
      ml={{
        base: '0',
        lg: '8',
      }}
      className="flex flex-col flex-grow-[2]"
    >
      <Box
        display={'flex'}
        flexDirection={{
          base: 'column-reverse',
          lg: 'row',
        }}
        justifyContent={'space-between'}
        alignItems={{
          base: 'flex-start',
          lg: 'center',
        }}
        mb={8}
      >
        <Box
          data-testid="interview-tabs"
          className="flex gap-x-2 md:gap-x-5 gap-y-3 flex-wrap"
          w={{
            base: 'full',
            lg: 'auto',
          }}
          mr={{
            base: 0,
            lg: 8,
          }}
        >
          <Button
            rounded={'1.25rem'}
            variant={'brand-solid-gray'}
            bgColor={currentTab === 'full' ? '#E0E0E0' : '#F5F5F5'}
            cursor={currentTab === 'full' ? 'default' : undefined}
            {...{
              ...(currentTab === 'full' && {
                _hover: {
                  bgColor: '',
                },
              }),
            }}
            onClick={(event) => {
              event.preventDefault()
              setCurrentTab('full')
            }}
            px={4}
          >
            <Box
              display={{
                base: 'none',
                md: 'block',
              }}
            >
              Raw Transcript
            </Box>
            <Box
              display={{
                base: 'block',
                md: 'none',
              }}
            >
              Transcript
            </Box>
          </Button>
          <Tooltip
            rounded={'1.25rem'}
            py={2}
            px={4}
            bgColor={'blackAlpha.900'}
            label={'Please upload recording of your interview first'}
            isDisabled={!!interview?.fullTranscript}
            shouldWrapChildren={true}
          >
            <Button
              rounded={'1.25rem'}
              variant={'brand-solid-gray'}
              bgColor={currentTab === 'short' ? '#E0E0E0' : '#F5F5F5'}
              isDisabled={isShortTranscriptDisabled}
              cursor={currentTab === 'short' ? 'default' : undefined}
              px={4}
              {...{
                ...(currentTab === 'short' && {
                  _hover: {
                    bgColor: '',
                  },
                }),
              }}
              onClick={async (event) => {
                event.preventDefault()
                try {
                  setCurrentTab('short')
                } catch (err) {
                  console.error(err)
                }
              }}
            >
              <MagicStarsIcon fillOpacity={0.5} />
              &nbsp;&nbsp;
              <Box
                display={{
                  base: 'none',
                  md: 'block',
                }}
              >
                Short Transcript{' '}
              </Box>
              <Box
                display={{
                  base: 'block',
                  md: 'none',
                }}
              >
                Answers
              </Box>
            </Button>
          </Tooltip>
          <div>
            <Tooltip
              rounded={'1.25rem'}
              py={2}
              px={4}
              bgColor={'blackAlpha.900'}
              label={
                interviewType === 'INTAKE'
                  ? 'Please add a company info and a role title'
                  : !candidate.data
                    ? 'Please add a candidate'
                    : 'Please wait for the transcript'
              }
              isDisabled={!isReportDisabled}
              shouldWrapChildren={true}
            >
              <Button
                rounded={'1.25rem'}
                minW={'7.5rem'}
                variant={'brand-solid-gray'}
                bgColor={currentTab === 'report' ? '#E0E0E0' : '#F5F5F5'}
                justifyContent={generateSummary.isPending ? 'center' : 'start'}
                isDisabled={
                  isReportDisabled || (isViewOnlyUser && !summaryParts?.length)
                }
                cursor={currentTab === 'report' ? 'default' : 'pointer'}
                isLoading={generateSummary.isPending}
                {...{
                  ...(currentTab === 'report' && {
                    _hover: {
                      bgColor: '',
                    },
                  }),
                }}
                onClick={async (event) => {
                  event.preventDefault()
                  try {
                    if (!summaryParts?.length) {
                      await generateSummary.mutateAsync()
                    }
                    setCurrentTab('report')
                  } catch (err) {
                    console.error(err)
                  }
                }}
              >
                <MagicStarsIcon fillOpacity={0.5} />
                &nbsp;&nbsp;
                {interviewType === 'INTAKE' ? 'Role brief' : 'Scorecard'}
              </Button>
            </Tooltip>
          </div>
        </Box>
        <Text
          fontWeight={700}
          color={'blackAlpha.500'}
          mb={{
            base: 4,
            lg: 0,
          }}
          ml={{
            base: 2,
            lg: 0,
          }}
        >
          {dateFromTimestamp(interview?.dateTimestamp || new Date())}
        </Text>
      </Box>
      {(() => {
        switch (currentTab) {
          case 'full':
            return <InputTranscript />
          case 'short':
            return <ShortTranscript />
          case 'report':
            return <Summary />
        }
      })()}
    </Box>
  )
}
